import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    pickPendingMilestone,
    pickMilestonePhrase,
    markMilestoneSeen,
    seedMilestoneChat,
} from "@/lib/milestones";
import { pickPendingUnlock, claimUnlock } from "@/lib/unlocks";

// Pacing guard: at most ONE ceremony per calendar day (Mochi's timezone).
// Surplus unlocks simply queue to the next day, so a very active user never
// gets pin + crown + hat in a single afternoon.
const CEREMONY_DAY_KEY = "mochi_ceremony_day";
const todayEastern = () =>
    new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const ceremonyFiredToday = () => {
    try { return localStorage.getItem(CEREMONY_DAY_KEY) === todayEastern(); } catch { return false; }
};
const stampCeremonyToday = () => {
    try { localStorage.setItem(CEREMONY_DAY_KEY, todayEastern()); } catch { /* storage unavailable */ }
};
const clearCeremonyStamp = () => {
    try { localStorage.removeItem(CEREMONY_DAY_KEY); } catch { /* storage unavailable */ }
};

/**
 * useMilestoneWatcher — fires AT MOST ONE milestone per session.
 *
 * Triggers at the start of a session (when settings first load) but only
 * if no other heavyweight surface (morning check-in, overwhelm overlay)
 * would compete. We let the milestone land FIRST when both are queued.
 *
 * Returns:
 *   { milestone, phrase }     — current milestone to render (or null)
 *   complete(): Promise<void> — call when the celebration finishes
 *   skipCheck:                  set true when this session has already
 *                               surfaced a milestone, to avoid re-firing
 *                               from rapid settings refetches.
 *
 * Single-fire semantics:
 *   - Once a milestone is picked, we mark it "armed" via a useRef so it
 *     stays consistent across re-renders until the user completes it.
 *   - We also stash a session-scoped flag so transient settings refetches
 *     don't immediately offer the next milestone in the same tab session.
 */
export function useMilestoneWatcher({ settings, enabled, onMoodOverride, onSettingsUpdate }) {
    const [active, setActive] = useState(null); // {milestone, phrase} | null
    const armedRef = useRef(null);
    const sessionFiredRef = useRef(false);

    // Month-iversary: Mochi borrows the party hat for the day — say so once.
    useEffect(() => {
        const prog = settings?.unlock_progress;
        if (!prog || prog.hat?.occasion !== "monthiversary" || prog.hat?.unlocked) return;
        const key = `mochi_monthiversary_${prog.days_known}`;
        try {
            if (localStorage.getItem(key)) return;
            localStorage.setItem(key, "1");
        } catch { /* storage unavailable — toast anyway */ }
        toast(`${prog.days_known / 30} month${prog.days_known > 30 ? "s" : ""} together 🎂 mochi borrowed the party hat for today`, { duration: 6000 });
    }, [settings?.unlock_progress]);

    useEffect(() => {
        if (!enabled) return;
        if (!settings) return;
        if (sessionFiredRef.current) return;
        if (active) return;
        if (ceremonyFiredToday()) return;

        // Earned wearables (pin / crown / hat) land before calendar notes.
        const unlock = pickPendingUnlock(settings);
        const pending = unlock || pickPendingMilestone(settings);
        if (!pending) return;
        const phrase = unlock ? unlock.phrase : pickMilestonePhrase(pending, settings);
        armedRef.current = unlock ? unlock.id : pending.day;
        sessionFiredRef.current = true;
        stampCeremonyToday();
        setActive({ milestone: pending, phrase });
        // Lock Mochi's mood to the celebration's starting state so the
        // avatar inside the overlay AND the small avatar below match.
        onMoodOverride?.(pending.initialMood, pending.celebrationMs + 600);
    }, [enabled, settings, active, onMoodOverride]);

    // Keep `complete` callback stable across renders so the celebration
    // overlay's setTimeout doesn't get torn down + re-created every render
    // (which would prevent the timer from ever firing).
    const onMoodOverrideRef = useRef(onMoodOverride);
    useEffect(() => { onMoodOverrideRef.current = onMoodOverride; }, [onMoodOverride]);
    const onSettingsUpdateRef = useRef(onSettingsUpdate);
    useEffect(() => { onSettingsUpdateRef.current = onSettingsUpdate; }, [onSettingsUpdate]);
    const activeRef = useRef(active);
    useEffect(() => { activeRef.current = active; }, [active]);

    const complete = useCallback(async () => {
        const current = activeRef.current;
        if (!current) return;
        setActive(null);
        try {
            if (current.milestone.kind === "unlock") {
                // Server re-checks eligibility, equips, and drops the chat note.
                const { settings: nextSettings } = await claimUnlock(current.milestone.id);
                if (nextSettings) onSettingsUpdateRef.current?.(nextSettings);
            } else {
                const day = current.milestone.day;
                const nextSettings = await markMilestoneSeen(day);
                if (nextSettings) onSettingsUpdateRef.current?.(nextSettings);
                await seedMilestoneChat(day);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Milestone persist failed:", e);
            sessionFiredRef.current = false;
            clearCeremonyStamp();
        }
        if (current.milestone.finalMood) {
            onMoodOverrideRef.current?.(current.milestone.finalMood, 4000);
        }
    }, []);

    return { milestone: active?.milestone || null, phrase: active?.phrase || null, complete };
}
